import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Dialog,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { RoadmapItem } from '@/models/RoadmapItem';
import { STATUS } from '@/constants/status';
import { getColorPaletteForStatus } from '@/utils/color-utils';
import ImageVideo from '../grid/ImageVideo';
import StatusChip from '../grid/StatusChip';
import LikeButton from '../grid/LikeButton';
import FollowButton from '../grid/FollowButton';
import Flyout from '../grid/Flyout';
import cwLogo from '@/assets/cw_logo.svg';
import cwLogoDark from '@/assets/cwlogo_dark.svg';
import cwLogoLight from '@/assets/cwlogo_light.svg';
import releaseNotes from '@/assets/release_notes.svg';
import './ListCard.css';

interface ListCardProps {
    data: RoadmapItem;
    onLike: (jiraId: string, isLiked: boolean) => void;
    onFollow: (jiraId: string, isFollowed: boolean) => void;
    isAuthenticated?: boolean;
    selectedJiraId?: string | null;
    onFlyoutClose?: () => void;
}

const ListCard: React.FC<ListCardProps> = ({
    data,
    onLike,
    onFollow,
    isAuthenticated = true,
    selectedJiraId,
    onFlyoutClose,
}) => {
    const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);

    useEffect(() => {
        if (selectedJiraId && data.jiraId === selectedJiraId) {
            setIsFlyoutOpen(true);
        }
    }, [selectedJiraId, data.jiraId]);

    const color = getColorPaletteForStatus(data.statusOnUI);
    const logoSrc = data.statusOnUI === STATUS.NEW_RELEASE ? cwLogoDark : cwLogoLight;

    const renderVideo = (videoId: string) => {
        return (
            <>
                <img
                    src={`https://img.youtube.com/vi/${videoId}/0.jpg`}
                    alt="video thumbnail"
                    className="list-card-thumbnail"
                />
                <PlayCircleIcon className="list-card-play-icon" />
            </>
        );
    };

    const renderImage = (imageSrc: string) => {
        return (
            <img
                src={imageSrc}
                alt="Card image"
                className={`list-card-image ${data.videoOrImageLink ? 'list-card-image-full' : 'list-card-image-logo'}`}
            />
        );
    };

    const renderImageOrVideo = (videoId: string | null) => {
        const videoOrImageLink = data.videoOrImageLink;
        if (!videoOrImageLink) return renderImage(logoSrc);

        const isVideo = videoOrImageLink.includes('youtube') || videoOrImageLink.includes('youtu.be');
        if (!isVideo) return renderImage(videoOrImageLink);

        return videoId ? renderVideo(videoId) : renderImage(logoSrc);
    };

    const handleFlyoutOpen = () => {
        setIsFlyoutOpen(true);
    };

    const handleFlyoutClose = () => {
        setIsFlyoutOpen(false);
        onFlyoutClose?.();
    };

    const handleCommunityClick = () => {
        window.open(data.virtualCommunity, '_blank');
    };

    const handleReleaseNotesClick = () => {
        window.open(data.releaseNotes, '_blank');
    };

    const showCommunityLink = data.virtualCommunity !== '' &&
        data.virtualCommunity.startsWith('https://virtualcommunity.connectwise.com');

    const itemWithColor = { ...data, color };

    return (
        <ImageVideo videoUrl={data.videoOrImageLink}>
            {({ handleOpen, videoId }) => (
                <Card className={`list-card list-card-border-${color}`} data-testid="list-card-component">
                    <Box className="list-card-layout">
                        <Box
                            className={`list-card-media list-card-media-${color}`}
                            onClick={videoId ? handleOpen : undefined}
                            style={{ cursor: videoId ? 'pointer' : 'default' }}
                        >
                            {renderImageOrVideo(videoId)}
                        </Box>

                        <CardContent className="list-card-content">
                            <Box className="list-card-row list-card-top-row">
                                <Typography variant="h6" className="list-card-title">
                                    {data.title}
                                </Typography>
                                <Typography variant="subtitle2" className="list-card-category">
                                    {data.category}
                                </Typography>
                                <Chip
                                    label={data.product}
                                    size="small"
                                    className="list-card-product-chip"
                                />
                                <StatusChip data={itemWithColor} />
                            </Box>

                            <Typography className="list-card-description">
                                {data.description}
                            </Typography>

                            <Box className="list-card-actions">
                                {isAuthenticated && (
                                    <LikeButton data={data} onLike={onLike} variant="grid" />
                                )}
                                {isAuthenticated && (
                                    <FollowButton data={data} onFollow={onFollow} variant="grid" />
                                )}

                                {showCommunityLink && (
                                    <Tooltip title="Community" placement="top">
                                        <Box className="list-card-link-container">
                                            <img
                                                src={cwLogo}
                                                alt="Community"
                                                onClick={handleCommunityClick}
                                                className="list-card-community-icon"
                                            />
                                        </Box>
                                    </Tooltip>
                                )}

                                {data.releaseNotes !== '' && (
                                    <Tooltip title="Release Notes" placement="top">
                                        <Box className="list-card-link-container">
                                            <img
                                                src={releaseNotes}
                                                alt="Release Notes"
                                                onClick={handleReleaseNotesClick}
                                                className="list-card-release-icon"
                                            />
                                        </Box>
                                    </Tooltip>
                                )}

                                <IconButton
                                    aria-label="View details"
                                    size="small"
                                    className="list-card-expand-button"
                                    onClick={handleFlyoutOpen}
                                >
                                    <AddOutlinedIcon />
                                </IconButton>
                            </Box>
                        </CardContent>
                    </Box>

                    {isFlyoutOpen && (
                        <Dialog
                            open={isFlyoutOpen}
                            onClose={handleFlyoutClose}
                            className="list-card-dialog"
                            PaperProps={{ className: 'list-card-dialog-paper' }}
                        >
                            <Flyout
                                onClose={handleFlyoutClose}
                                data={itemWithColor}
                                onLike={onLike}
                                onFollow={onFollow}
                                isAuthenticated={isAuthenticated}
                            />
                        </Dialog>
                    )}
                </Card>
            )}
        </ImageVideo>
    );
};

export default ListCard;
